import React from 'react';
import { 
  CalendarIcon, 
  CurrencyDollarIcon, 
  UserGroupIcon, 
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const StatCard = ({ title, value, icon: Icon, color, description }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon className={`h-6 w-6 ${color.replace('bg-', 'text-')}`} />
      </div>
      <div className="ml-4 flex-1">
        <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">{title}</p>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-black text-gray-900">{value}</p>
          {description && <span className="text-xs text-gray-500 font-medium">{description}</span>}
        </div>
      </div>
    </div>
  </div>
);

const StatsCards = ({ stats, userRole, business }) => {
  if (!stats) return null;

  const cards = [];
  const currencySymbol = business?.currency_symbol || '$';

  if (userRole === 'business_owner') {
    cards.push(
      { 
        title: 'Total Bookings', 
        value: stats.total_bookings || 0, 
        icon: CalendarIcon, 
        color: 'bg-blue-600',
        description: 'Lifetime'
      },
      { 
        title: 'Active Bookings', 
        value: stats.active_bookings || 0, 
        icon: ClockIcon, 
        color: 'bg-indigo-600',
        description: 'Confirmed'
      },
      { 
        title: 'Pending Action', 
        value: stats.pending_bookings || 0, 
        icon: ExclamationCircleIcon, 
        color: 'bg-amber-500',
        description: 'Needs response'
      },
      { 
        title: 'Resolved', 
        value: stats.completed_bookings || 0, 
        icon: CheckCircleIcon, 
        color: 'bg-emerald-600',
        description: 'Completed'
      },
      { 
        title: 'Cancelled', 
        value: stats.cancelled_bookings || 0, 
        icon: XCircleIcon, 
        color: 'bg-rose-600',
        description: 'Total'
      },
      { 
        title: 'Total Revenue', 
        value: `${currencySymbol}${(stats.total_revenue || 0).toLocaleString()}`, 
        icon: CurrencyDollarIcon, 
        color: 'bg-emerald-600',
        description: 'Completed'
      },
      { 
        title: 'Customers', 
        value: stats.unique_customers || 0, 
        icon: UserGroupIcon, 
        color: 'bg-indigo-600',
        description: 'Total unique'
      }
    );
  } else if (userRole === 'customer') {
    cards.push(
      { 
        title: 'Total Bookings', 
        value: stats.total_bookings || 0, 
        icon: CalendarIcon, 
        color: 'bg-blue-600',
        description: 'Lifetime'
      },
      { 
        title: 'Upcoming Slot', 
        value: stats.upcoming_bookings || 0, 
        icon: ClockIcon, 
        color: 'bg-emerald-600',
        description: 'Pending/Confirmed'
      },
      { 
        title: 'Resolved', 
        value: stats.completed_bookings || 0, 
        icon: CheckCircleIcon, 
        color: 'bg-indigo-600',
        description: 'Completed'
      },
      { 
        title: 'Cancelled', 
        value: stats.cancelled_bookings || 0, 
        icon: XCircleIcon, 
        color: 'bg-rose-600',
        description: 'Total'
      }
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => (
        <StatCard key={index} {...card} />
      ))}
    </div>
  );
};

export default StatsCards;
